import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SchemaJsonLd from '../../components/base/SchemaJsonLd';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Services from './components/Services';
import About from './components/About';
import Testimonials from './components/Testimonials';
import Footer from './components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://fmacrew.com';

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': ['LocalBusiness', 'GeneralContractor'],
  name: 'FMA Crew',
  alternateName: 'Facility Maintenance of America',
  url: SITE_URL,
  telephone: '+14702225777',
  email: 'office@fmacrew.com',
  logo: `${SITE_URL}/logo.png`,
  image: `${SITE_URL}/og-image.jpg`,
  description:
    'FMA Crew (Facility Maintenance of America) is Greater Atlanta\'s trusted specialist in multi-family housing and apartment maintenance — offering unit turns, common area upkeep, balcony restoration, waterproofing, structural repairs, and preventive maintenance programs for apartment communities.',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Norcross',
    addressRegion: 'GA',
    postalCode: '30093',
    addressCountry: 'US',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 33.9412,
    longitude: -84.2135,
  },
  areaServed: [
    { '@type': 'City', name: 'Norcross' },
    { '@type': 'City', name: 'Marietta' },
    { '@type': 'City', name: 'Alpharetta' },
    { '@type': 'City', name: 'Decatur' },
    { '@type': 'City', name: 'Roswell' },
    { '@type': 'City', name: 'Sandy Springs' },
    { '@type': 'City', name: 'Duluth' },
    { '@type': 'City', name: 'Lawrenceville' },
    { '@type': 'City', name: 'Atlanta' },
  ],
  priceRange: '$$',
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '17:00',
    },
  ],
  hasMap: 'https://maps.google.com/?q=Norcross,GA',
  sameAs: [],
  serviceType: [
    'Multi-Family & Apartment Maintenance',
    'Apartment Unit Turns & Make-Readys',
    'Common Area Maintenance',
    'Balcony & Stairwell Restoration',
    'Waterproofing & Structural Repairs',
    'Exterior & Facade Repairs',
    'Preventive Maintenance Programs',
    'Residential Remodeling',
    'Commercial Remodeling',
    'Emergency Maintenance',
  ],
};

const webSiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'FMA Crew',
  url: SITE_URL,
  description:
    'FMA Crew — Greater Atlanta\'s specialist in multi-family & apartment maintenance, residential remodeling, and commercial remodeling. Licensed, insured, and 5-star rated.',
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/?s={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What does your full-service apartment maintenance include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Our full-service apartment maintenance covers everything — unit turns and make-readys, common area upkeep, exterior and facade repairs, balcony and stairwell restoration, waterproofing, structural repairs, and preventive maintenance programs. We serve apartment communities of all sizes across Greater Atlanta.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do you handle unit turns and make-readys for apartment communities?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We deliver fast, consistent unit make-readys that minimize vacancy time. Our process covers painting, flooring repairs, drywall patching, fixture replacements, and a full make-ready checklist — all executed to a uniform standard across every unit, on your lease calendar schedule.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can you manage maintenance for a large portfolio of apartment communities?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes — FMA Crew is structured to serve apartment communities of any size, from single properties to large multi-community portfolios across the Greater Atlanta area. We provide one dedicated point of contact, consistent quality standards, and scalable crew capacity to match your portfolio\'s needs.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do you offer emergency maintenance services for apartment communities?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes — we provide 24/7 emergency response for urgent issues like burst pipes, water intrusion, structural hazards, HVAC failures, and roof leaks. Our emergency line is always on, and we deploy within 2–4 hours to protect your residents and your asset.',
      },
    },
    {
      '@type': 'Question',
      name: 'What preventive maintenance programs do you offer for apartments?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We design customized preventive maintenance contracts for apartment communities — available on monthly, quarterly, or semi-annual schedules. Programs cover HVAC, plumbing checks, exterior inspections, common area upkeep, and more. Preventive programs reduce emergency repair costs by up to 60% and extend asset life significantly.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do you handle balcony waterproofing and structural restoration for apartment buildings?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes — balcony waterproofing and structural restoration is one of our core specialties. We perform full restoration of balconies, stairwells, and railings including waterproofing membranes, surface repairs, and safety compliance checks. Work is phased to minimize disruption to residents.',
      },
    },
  ],
};

export default function Home() {
  const location = useLocation();

  useEffect(() => {
    const state = location.state as { scrollTo?: string } | null;
    if (state?.scrollTo) {
      const timer = setTimeout(() => {
        const el = document.querySelector(state.scrollTo as string);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  return (
    <>
      <title>FMA Crew | Apartment Management &amp; Remodeling Atlanta GA</title>
      <meta
        name="description"
        content="FMA Crew (Facility Maintenance of America) offers full-service apartment management, residential remodeling, and commercial remodeling across Greater Atlanta, GA. Licensed, insured, and 5-star rated."
      />
      <link rel="canonical" href={`${SITE_URL}/`} />
      <SchemaJsonLd schema={localBusinessSchema} id="schema-local-business" />
      <SchemaJsonLd schema={webSiteSchema} id="schema-website" />
      <SchemaJsonLd schema={faqSchema} id="schema-faq" />
      <main className="min-h-screen bg-white">
        <Navbar />
        <Hero />
        <Services />
        <About />
        <Testimonials />
        <Footer />
      </main>
    </>
  );
}
